import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Badge } from "reactstrap";
import { Link as RouteLink } from "react-router-dom";
import { AppContext } from "../../context/app";
import { checkIfHasAccessToRoute } from "../../routes/util";
import { RouteChildren, Route } from "../../routes/routes-list";
import { CommittedClubEvent } from "../../pages/CommittedClub/CommittedClubEvents";
import { CompanyService } from "@pushpress/committed-club-typescript-sdk";
import { configureSDK } from "../../pages/CommittedClub/utils";
import { interop_trackEvent } from "../../utils/analytics.interop";

export type CommittedClubSidebarItem = Partial<RouteChildren & Route>;

const CommittedClubSidebarItem = ({
  name,
  path: to,
  icon: Icon,
  badgeColor,
  badgeText,
  target,
}: CommittedClubSidebarItem) => {
  const [installed, setInstalled] = useState(false);
  const { user, spa } = useContext(AppContext);

  const getCompanyStatus = useCallback(() => {
    CompanyService.getCompanyStatus().then((result) => {
      if (!result.error) {
        setInstalled(result.data.company.installed);
      }
    });
  }, []);

  useEffect(() => {
    configureSDK();
    getCompanyStatus();
  }, [getCompanyStatus]);

  const Link = useCallback(
    ({ children }: { children: ReactNode }) => {
      const trackOpen = () => {
        interop_trackEvent(CommittedClubEvent.CommittedClubDashboardOpened, {
          app: "committed_club",
          page: `side_nav`,
        });
      };

      // If app is inside SPA and next route is an React page we can use NavLink to navigate
      if (spa) {
        return (
          <RouteLink to={to} className="sidebar-link" onClick={trackOpen}>
            {children}
          </RouteLink>
        );
      }

      // If not, it's an embed from PHP, so we should just use regular <a> tag
      return (
        <a
          href={to}
          className="sidebar-link active"
          target={target}
          onClick={trackOpen}
        >
          {children}
        </a>
      );
    },
    [spa, target, to],
  );

  const getSidebarItemClass = useCallback((path) => {
    return window.location.pathname === path ? "active" : "";
  }, []);

  // Hide item if app is still being setup
  if (!installed) return <></>;

  if (!user) return <></>;

  // Check if there is permission to show the sidebar item
  if (!checkIfHasAccessToRoute(user, to)) return <></>;

  return (
    <li className={`sidebar-item ${getSidebarItemClass(to)}`}>
      <Link>
        {Icon && (
          <Icon
            className="align-middle mr-2"
            color="white"
            style={{
              width: "18px",
              height: "18px",
            }}
          />
        )}
        {name}
        {badgeColor && badgeText && (
          <Badge color={badgeColor} size={18} className="sidebar-badge">
            {badgeText}
          </Badge>
        )}
      </Link>
    </li>
  );
};

export default CommittedClubSidebarItem;
